import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import type { ElasticVehiclesResponse } from '@//app/api/search/vehicles';
import type { Market } from '@//config/domain-locale-map';
import type {
  ElasticSearchResponseFacet,
  ElasticSearchResponseFacetFilter,
} from '@//ElasticSearch';
import { resolveAvailableFilters } from '@//utils/vehicles-search/market-specifc-filters';

export interface EnrichedElasticSearchResponseFacetFilter extends ElasticSearchResponseFacetFilter {
  currentCount: number;
}

export type EnrichedElasticSearchResponseFacetType = 'spatial' | 'checkbox' | 'range' | 'toggle';

const facetTypeMap = {
  location: 'spatial',
  manufacturer: 'checkbox',
  gearboxType: 'checkbox',
  modelSeries: 'checkbox',
  chassis: 'checkbox',
  color: 'checkbox',
  drivingWheel: 'checkbox',
  environmentClass: 'checkbox',
  'properties.segment': 'checkbox',
  branches: 'checkbox',
  inventoryStatus: 'checkbox',
  engineBaseType: 'checkbox',
  fuelType: 'checkbox',
  fuelTypes: 'checkbox',
  price: 'range',
  modelYear: 'range',
  mileage: 'range',
  odometerValueAsKm: 'range',
  hasManufacturerPackaging: 'toggle',
  deductibleVat: 'toggle',
  mrf: 'toggle',
  isAuthorizedResellerForManufacturer: 'toggle',
  hasMedia: 'toggle',
  ecom: 'toggle',
  leasing: 'toggle',
  businessLeasing: 'toggle',
  isAuctionItem: 'toggle',
  enginePower: 'range',
  'enhancedProperties.electricalRangeWLTP.value': 'range',
  'properties.seats': 'range',
  'properties.co2': 'range',
} as const;

export type SpatialFacetKeys = {
  [K in keyof typeof facetTypeMap]: (typeof facetTypeMap)[K] extends 'spatial' ? K : never;
}[keyof typeof facetTypeMap];

export type CheckboxFacetKeys = {
  [K in keyof typeof facetTypeMap]: (typeof facetTypeMap)[K] extends 'checkbox' ? K : never;
}[keyof typeof facetTypeMap];

export type RangeFacetKeys = {
  [K in keyof typeof facetTypeMap]: (typeof facetTypeMap)[K] extends 'range' ? K : never;
}[keyof typeof facetTypeMap];

export type ToggleFacetKeys = {
  [K in keyof typeof facetTypeMap]: (typeof facetTypeMap)[K] extends 'toggle' ? K : never;
}[keyof typeof facetTypeMap];

export type FacetKey = keyof typeof facetTypeMap;

const resolveType = (id: FacetKey): EnrichedElasticSearchResponseFacetType => facetTypeMap[id];

export interface EnrichedElasticSearchResponseFacet extends ElasticSearchResponseFacet {
  type?: EnrichedElasticSearchResponseFacetType;
  filters: EnrichedElasticSearchResponseFacetFilter[];
}

interface UseEnrichedFacets {
  initial?: ElasticVehiclesResponse;
  current?: ElasticVehiclesResponse;
  market: Market;
}

const useEnrichedFacets = ({
  initial,
  current,
  market,
}: UseEnrichedFacets): EnrichedElasticSearchResponseFacet[] | undefined => {
  const t = useTranslations('utils.search');

  // 1) Which facets are available for this market, and their sort order
  const { list: availableList, indexMap } = useMemo(() => {
    const list = resolveAvailableFilters(market);
    const indexMap = new Map<string, number>();
    list.forEach((id, idx) => indexMap.set(id, idx));
    return { list, indexMap };
  }, [market]);

  // 2) Build a lookup of current facet filters by facet.id → displayName → filter
  const currentMap = useMemo(
    () =>
      current?.facets
        .filter((f) => availableList.includes(f.id))
        .reduce<Record<string, Record<string, ElasticSearchResponseFacetFilter>>>((acc, f) => {
          acc[f.id] = Object.fromEntries(f.filters.map((x) => [x.displayName, x]));
          return acc;
        }, {}) ?? {},
    [current, availableList],
  );

  // 3) Translation labels for each facet
  const labels = useMemo(
    () =>
      initial?.facets
        .filter((f) => availableList.includes(f.id))
        .reduce<Record<string, string>>((acc, f) => {
          const key = f.id.replace(/\./g, '_') as FacetKey;
          acc[f.id] = t(`filters.${key as 'manufacturer'}.displayName`);
          return acc;
        }, {}) ?? {},
    [initial, t, availableList],
  );

  // 4) Extract current query flags for toggles (default false)
  const currentQuery = useMemo(
    () =>
      // Assume current.query holds raw filter flags
      // Replace 'query' with the correct path if different
      (current as any)?.query ?? {},
    [current],
  );

  // 5) Enrich and merge initial + current filters, then append toggles
  return useMemo(() => {
    if (!initial) return undefined;

    // checkbox & range facets
    const enrichedFacets = initial.facets
      .filter((f) => availableList.includes(f.id) && resolveType(f.id as FacetKey) !== 'toggle')
      .sort((a, b) => (indexMap.get(a.id) ?? 0) - (indexMap.get(b.id) ?? 0))
      .map((facet) => {
        const initialFilters = facet.filters;
        const currFiltersMap = currentMap[facet.id] || {};

        // Collect all displayNames
        const allDisplayNames = Array.from(
          new Set<string>([
            ...initialFilters.map((f) => f.displayName),
            ...Object.keys(currFiltersMap),
          ]),
        );

        const enrichedFilters: EnrichedElasticSearchResponseFacetFilter[] = allDisplayNames
          .map((displayName) => {
            const init = initialFilters.find((f) => f.displayName === displayName);
            const curr = currFiltersMap[displayName];

            if (init) {
              return {
                ...init,
                query: curr?.query ?? init.query,
                currentCount: curr?.count ?? 0,
              };
            }
            if (curr) {
              return {
                ...curr,
                currentCount: curr.count,
              };
            }
            return {
              displayName,
              query: '',
              count: 0,
              selected: false,
              children: [],
              currentCount: 0,
            };
          })
          .sort((a, b) => b.currentCount - a.currentCount || b.count - a.count);

        return {
          ...facet,
          displayName: labels[facet.id],
          type: resolveType(facet.id as FacetKey),
          filters: enrichedFilters,
        };
      });

    // toggle facets, empty filters, used only as placeholder, selected is resolved elsewhere
    const toggleFacets: EnrichedElasticSearchResponseFacet[] = (
      Object.keys(facetTypeMap) as FacetKey[]
    )
      .filter((id) => availableList.includes(id) && resolveType(id) === 'toggle')
      .map((id) => ({
        id,
        displayName: t(`filters.${id as ToggleFacetKeys}.displayName`),
        type: 'toggle',
        filters: [],
        selectionType: 'SINGLE',
        multipleLevels: false,
      }));

    // location facet, empty filter, used only as placeholder, selected is resolved elsewhere
    const locationFacet: EnrichedElasticSearchResponseFacet[] = availableList.includes('location')
      ? [
          {
            id: 'location',
            displayName: t('filters.location.displayName'),
            type: 'spatial' as EnrichedElasticSearchResponseFacetType,
            filters: [],
            selectionType: 'SINGLE',
            multipleLevels: false,
          },
        ]
      : [];

    // Combine and sort: keep original order then toggles at end
    return [...locationFacet, ...enrichedFacets, ...toggleFacets];
  }, [initial, availableList, indexMap, currentMap, labels, currentQuery, t]);
};

export default useEnrichedFacets;
